/**
 * MotoTrips - Form Styling Standards
 * =====================================
 * 
 * REGRAS DE UI PARA FORMULÁRIOS:
 * 
 * 0. ÍCONES PROIBIDOS
 *    ⚠️ NUNCA usar o ícone "Bike" do Lucide - parece uma bicicleta, não uma mota!
 *    - Para representar motas, usar: Gauge (velocímetro)
 *    - Para garagem, usar: Warehouse
 *    - Para placeholder sem foto de mota, usar: Gauge ou imagem placeholder
 * 
 * 1. INPUTS/DROPDOWNS
 *    - Fundo mais escuro que o card envolvente
 *    - Border visível (border-border/60)
 *    - Hover: border mais forte
 *    - Focus: border com cor primária
 *    
 * 2. CARDS CLICÁVEIS
 *    - Fundo mais escuro que o card pai (bg-muted/50)
 *    - Hover: fundo ainda mais escuro
 *    - Border subtil com hover
 *    
 * 3. TOOLTIPS
 *    - Todos os botões, ícones clicáveis e badges devem ter tooltip
 *    - Usar TooltipProvider + Tooltip + TooltipTrigger + TooltipContent
 *    - Delay padrão: 200ms
 *    
 * 4. BOTÕES "GUARDAR/GRAVAR/ALTERAR" (SAVE BUTTONS)
 *    ⚠️ REGRA OBRIGATÓRIA: Usar variant="save"
 *    - Estado DESATIVADO: cinzento escuro, pouco visível
 *    - Estado ATIVO: laranja vibrante (cor primária), com shadow
 *    - Transição visual clara entre estados
 *    - ⚠️ ALINHAMENTO: SEMPRE à direita (usar formStyles.saveButtonContainer)
 *    - ⚠️ POSIÇÃO: SEMPRE no fundo do formulário (NUNCA no topo ou meio)
 *    
 *    Uso:
 *    ```jsx
 *    <div className={formStyles.saveButtonContainer}>
 *      <Button variant="save" disabled={!hasChanges || saving}>
 *        {saving ? <Loader2 className="animate-spin" /> : <Save />}
 *        Guardar
 *      </Button>
 *    </div>
 *    ```
 *    
 * 5. BOTÕES "ELIMINAR/SAIR"
 *    - Sempre com AlertDialog de confirmação
 *    - Estilo: text-muted-foreground hover:text-destructive
 * 
 * 6. BOTÕES EM GERAL
 *    ⚠️ NUNCA usar className="w-full" em botões de ação
 *    - Botões de ação devem ter tamanho automático baseado no conteúdo
 *    - Alinhar à direita com flex justify-end
 *    - Excepção: Botões dentro de DialogFooter (alinhamento é gerido pelo Dialog)
 * 
 * 7. MENUS DE NAVEGAÇÃO (TABS)
 *    - Usar estilo de barra horizontal (igual ao AdminPanel)
 *    - Container: bg-card/90 backdrop-blur-sm border border-border rounded-md
 *    - Título da secção com ícone à esquerda, separado por "|"
 *    - Botões de tab: hover:bg-muted, activo: bg-primary/20 text-primary font-medium
 *    
 *    Exemplo:
 *    ```jsx
 *    <div className={formStyles.tabBar}>
 *      <span className={formStyles.tabBarTitle}>
 *        <Icon className="h-4 w-4" /> Título
 *      </span>
 *      <span className="text-muted-foreground">|</span>
 *      <button className={activeTab === "tab1" ? formStyles.tabActive : formStyles.tabInactive}>
 *        Tab 1
 *      </button>
 *    </div>
 *    ```
 * 
 * USO:
 * ```jsx
 * import { formStyles, checkFormChanges, TOOLTIP_DELAY } from "@/lib/form-styles";
 * 
 * // Inputs
 * <Input className={formStyles.input} />
 * <Textarea className={formStyles.textarea} />
 * <SelectTrigger className={formStyles.select} />
 * 
 * // Cards clicáveis
 * <div className={formStyles.cardClickable}>...</div>
 * 
 * // Botão guardar (USA SEMPRE variant="save")
 * <Button variant="save" disabled={!hasChanges}>Guardar</Button>
 * 
 * // Tracking de alterações
 * const hasChanges = checkFormChanges(initialData, formData);
 * ```
 */

export const formStyles = {
  // ===== INPUT FIELDS =====
  // Text, number, email, password, etc.
  input: "bg-background/80 !border !border-zinc-600 hover:!border-primary/50 focus:!border-primary",
  
  // Textarea fields
  textarea: "bg-background/80 !border !border-zinc-600 hover:!border-primary/50 focus:!border-primary",
  
  // Select/Dropdown trigger
  select: "bg-background/80 !border !border-zinc-600 hover:!border-primary/50 focus:!border-primary",
  
  // ===== CARDS =====
  // Clickable card within another card
  cardClickable: "bg-muted/50 hover:bg-muted/70 border border-border/40 hover:border-border/60 transition-colors cursor-pointer",
  
  // Nested card (non-clickable, visually distinct)
  cardNested: "bg-muted/30 border border-border/30",
  
  // Card for list items (members, files, etc.)
  cardListItem: "bg-muted/50 rounded-lg p-3",
  
  // ===== SELECTABLE OPTION CARDS =====
  // Base style for selectable option cards (like radio buttons but as cards)
  optionCard: "p-4 rounded-xl border-2 transition-all text-left cursor-pointer bg-background border-white/20 hover:border-white/40",
  
  // Selected state variants for option cards
  optionCardSelected: {
    green: "border-green-500 bg-green-500/20",
    yellow: "border-yellow-500 bg-yellow-500/20", 
    red: "border-red-500 bg-red-500/20",
    primary: "border-primary bg-primary/20",
    blue: "border-blue-500 bg-blue-500/20",
  },
  
  // ===== BUTTONS =====
  // Destructive action button (delete, leave, etc.)
  btnDestructive: "text-muted-foreground hover:text-destructive transition-colors",
  
  // Save button base class
  btnSave: "transition-opacity disabled:opacity-50 disabled:cursor-not-allowed",
  
  // ===== SAVE BUTTON CONTAINER =====
  // ⚠️ REGRA: Botões de guardar SEMPRE alinhados à direita
  saveButtonContainer: "flex justify-end pt-4",
  
  // ===== TAB NAVIGATION =====
  // Container da barra de tabs horizontal
  tabBar: "flex items-center gap-1 p-1 bg-card/90 backdrop-blur-sm border border-border rounded-md overflow-x-auto",
  
  // Wrapper para barras de tabs (quando há múltiplas barras consecutivas)
  // Usar mb-px entre barras consecutivas, mb-6 antes do conteúdo final
  tabBarWrapper: "flex items-center gap-2",
  tabBarWrapperConsecutive: "flex items-center gap-2 mb-px", // Entre barras consecutivas
  tabBarWrapperFinal: "flex items-center gap-2 mb-6", // Antes do conteúdo
  
  // Barra interna com flex-1
  tabBarInner: "flex-1 flex items-center gap-1 p-1 bg-card/90 backdrop-blur-sm border border-border rounded-md overflow-x-auto",
  
  // Título da secção na barra de tabs
  tabBarTitle: "px-3 py-1.5 text-sm font-semibold flex items-center gap-1 text-foreground min-w-fit",
  
  // Separador vertical entre título e tabs
  tabBarSeparator: "text-muted-foreground mx-1",
  
  // Botão de tab activo
  tabActive: "px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap bg-primary/20 text-primary font-medium",
  
  // Botão de tab inactivo
  tabInactive: "px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap hover:bg-muted text-muted-foreground",

  // ===== OVERLAY ACTION BUTTONS =====
  // Botões de acção em overlays de cards (estilo menu dropdown escuro)
  // Usar quando há botões sobrepostos em imagens ou cards
  // O card pai deve ter classe "group" para o hover funcionar
  overlayButton: "flex items-center gap-2 px-3 py-1.5 bg-zinc-800/95 hover:bg-zinc-700 text-white text-xs font-medium rounded-md border border-transparent hover:border-primary transition-colors",
  
  // Container para overlay buttons (centrado verticalmente, aparece só no hover do card pai)
  // Requer classe "group" no elemento pai
  overlayButtonContainer: "absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/40 p-4 opacity-0 group-hover:opacity-100 transition-opacity",
  
  // Imagem com blur no hover (usar no elemento img ou div de imagem)
  // Requer classe "group" no elemento pai
  overlayImageBlur: "w-full h-full object-cover transition-all duration-300 group-hover:blur-[2px] group-hover:scale-105",
  
  // Imagem com zoom no hover mas sem blur
  overlayImageZoom: "w-full h-full object-cover transition-transform duration-300 group-hover:scale-105",

  // ===== DATA STATUS BADGES =====
  // Badge de estado de dados (completo/incompleto) para passeios, motas, etc.
  // ⚠️ REGRA: Sempre usar com Tooltip para explicar o significado
  //
  // Uso:
  // ```jsx
  // <TooltipProvider>
  //   <Tooltip>
  //     <TooltipTrigger asChild>
  //       <span className={dataOk ? formStyles.dataOkBadge : formStyles.dataIncompleteBadge}>
  //         {dataOk ? <Check className="h-3 w-3" /> : <ThumbsDown className="h-3 w-3" />}
  //         {dataOk ? t("data_ok") : t("data_incomplete")}
  //       </span>
  //     </TooltipTrigger>
  //     <TooltipContent>{tooltip}</TooltipContent>
  //   </Tooltip>
  // </TooltipProvider>
  // ```
  dataOkBadge: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500/90 text-white",
  dataIncompleteBadge: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-background/80 backdrop-blur-sm text-amber-500 border border-amber-500/50",

  // ===== ACTION BAR (BARRA DE AÇÕES NO TOPO) =====
  // Barra translúcida para ações principais (Garagem, Novo Passeio, Editar, etc.)
  // ⚠️ REGRA: TODOS os botões na action bar DEVEM ter tooltip
  // 
  // Uso:
  // ```jsx
  // <div className={formStyles.actionBar}>
  //   <div className={formStyles.actionBarContainer}>
  //     <TooltipProvider>
  //       <Tooltip>
  //         <TooltipTrigger asChild>
  //           <Button className={formStyles.actionBarButton}>
  //             <Icon /> Label
  //           </Button>
  //         </TooltipTrigger>
  //         <TooltipContent>{t("tooltip_key")}</TooltipContent>
  //       </Tooltip>
  //     </TooltipProvider>
  //   </div>
  // </div>
  // ```
  actionBar: "border-b border-border bg-card/50 backdrop-blur-sm relative z-20",
  actionBarContainer: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-end gap-2 h-12",
  actionBarButton: "border-border font-bold uppercase tracking-wide bg-background/80 hover:bg-background hover:border-primary",

  // ===== SWITCH TOGGLE (PADRÃO STANDARD) =====
  // ⚠️ REGRA OBRIGATÓRIA: Switches NUNCA devem ter Badge ao lado
  // - Usar APENAS tooltip para indicar estado/acção
  // - O tooltip deve ser contextual: mostrar o que VAI acontecer ao clicar
  // - Wrapper div necessário para o TooltipTrigger funcionar com Switch
  //
  // Uso:
  // ```jsx
  // <TooltipProvider>
  //   <Tooltip>
  //     <TooltipTrigger asChild>
  //       <div className="flex items-center">
  //         <Switch
  //           checked={isEnabled}
  //           onCheckedChange={(checked) => handleToggle(checked)}
  //           data-testid="my-toggle"
  //         />
  //       </div>
  //     </TooltipTrigger>
  //     <TooltipContent>
  //       <p>{isEnabled ? t('tooltips.disable') : t('tooltips.enable')}</p>
  //     </TooltipContent>
  //   </Tooltip>
  // </TooltipProvider>
  // ```
  //
  // ❌ NÃO FAZER:
  // ```jsx
  // <Badge>{isEnabled ? "Ativo" : "Inativo"}</Badge>  // PROIBIDO!
  // <Switch ... />
  // ```
  switchWrapper: "flex items-center",
};

/**
 * Combine form style with additional classes
 * @param {string} baseStyle - Style from formStyles
 * @param {string} additionalClasses - Additional Tailwind classes
 * @returns {string} Combined class string
 */
export const combineStyles = (baseStyle, additionalClasses = "") => {
  return `${baseStyle} ${additionalClasses}`.trim();
};

/**
 * Standard tooltip delay (ms)
 */
export const TOOLTIP_DELAY = 200;

/**
 * Check if form has changes compared to initial state
 * 
 * @param {Object} initial - Initial form data (saved when data loads)
 * @param {Object} current - Current form data
 * @returns {boolean} True if form has changes
 * 
 * @example
 * const [initialData, setInitialData] = useState(null);
 * const [formData, setFormData] = useState({});
 * 
 * // On data load:
 * useEffect(() => {
 *   if (data) {
 *     setInitialData(data);
 *     setFormData(data);
 *   }
 * }, [data]);
 * 
 * // In JSX:
 * <Button disabled={!checkFormChanges(initialData, formData)}>Guardar</Button>
 */
export const checkFormChanges = (initial, current) => {
  if (!initial || !current) return false;
  return JSON.stringify(initial) !== JSON.stringify(current);
};

export default formStyles;
